/* global btoa */
import { extendObservable, observable, runInAction } from 'mobx';
import * as mobxUtils from 'mobx-utils';
import numeral from 'numeral';
import config from './config';

function wrap (name) {
  return function (...args) {
    const fn = name ? mobxUtils.fromPromise[name] : mobxUtils.fromPromise;
    const ob = fn(...args);

    extendObservable(ob, {
      get isPending () {
        return ob.state === mobxUtils.PENDING;
      },

      get isFulfilled () {
        return ob.state === mobxUtils.FULFILLED;
      },

      get isRejected () {
        return ob.state === mobxUtils.REJECTED;
      }
    });

    return ob;
  };
}

export const fromPromise = wrap();

// Amounts coming from the API can be null or non-numeric strings; numeral
// throws ("toString of null") when fed those, so coerce before doing math.
export const parseAmount = value => {
  const number = parseFloat(value);
  return isNaN(number) ? 0 : number;
};

export const moneyFormat = (amount, currency, format = '0,0[.]00', hideCurrency) => {
  let formattedAmount;

  if (!amount || (amount && amount < 0.01)) {
    amount = 0;
  }

  if (currency === 'GBP') {
    formattedAmount = numeral(amount).format(format);
    formattedAmount = `£${formattedAmount}`;
  } else if (currency === 'EUR') {
    formattedAmount = numeral(amount).format(format);
    formattedAmount = `€${formattedAmount}`;
  } else {
    formattedAmount = numeral(amount).format(format);
    formattedAmount = `$${formattedAmount}`;
  }

  return `${formattedAmount} ${hideCurrency ? '' : currency}`;
};

Object.assign(fromPromise, mobxUtils.fromPromise, {
  resolve: wrap('resolve'),
  reject: wrap('reject')
});

export const currencySymbol = (name) => {
  const currency = currencyFromProtocol(name);
  return currency ? currency.symbol : '';
};

export const currencyDisplaySymbol = (name) => {
  const currency = currencyFromProtocol(name);
  return currency ? currency.displaySymbol : '';
};

export const networkServicePath = (name) => {
  const currency = currencyFromProtocol(name);
  return currency ? currency.servicePath : '';
};

export const isCompleted = (charge) => {
  return charge.status === 'success' || charge.status === 'overpaid' || charge.status === 'resolved';
};

export const hasPayment = (charge) => {
  return charge.payments && !!Object.keys(charge.payments).length;
};

export const getPaymentCurrency = (charge) => {
  return Object.keys(charge.payments)[0];
};

export const getPaymentAmount = (charge, includeCurrencySymbol = true) => {
  const currency = getPaymentCurrency(charge);

  const paymentAmount = charge.payments[currency] ? charge.payments[currency].balance : 0;
  const symbol = currencyDisplaySymbol(currency);

  return includeCurrencySymbol ? `${paymentAmount} ${symbol}` : paymentAmount;
};

export const getQuoteAmount = (charge, includeCurrencySymbol = true) => {
  let currency;

  if (hasPayment(charge)) {
    currency = getPaymentCurrency(charge);
  }

  const quotedAmount = currency && charge.quotes[currency] ? charge.quotes[currency].amount : 0;
  const symbol = currencyDisplaySymbol(currency);

  return includeCurrencySymbol ? `${quotedAmount} ${symbol}` : quotedAmount;
};

const getPaidCryptoTotal = (charge) => {
  if (!hasPayment(charge)) return parseAmount(getQuoteAmount(charge, false));

  const currency = getPaymentCurrency(charge);
  const txs = charge.payments[currency]?.transactions || [];

  if (txs.length) {
    return txs.reduce((sum, tx) => sum + parseAmount(tx.amount), 0);
  }

  const quote = parseAmount(getQuoteAmount(charge, false));
  return quote || parseAmount(getPaymentAmount(charge, false));
};

const getPaymentRefundTotal = (charge) => {
  if (!hasPayment(charge)) return 0;

  return Object.keys(charge.payments).reduce((sum, protocol) => (
    sum + parseAmount(charge.payments[protocol].refund)
  ), 0);
};

export const getRefundSummary = (charge) => {
  const refundEvents = (charge.timeline || []).filter(event => event.type === 'CHARGE_REFUND');
  const paymentRefunded = getPaymentRefundTotal(charge);
  const eventRefunded = refundEvents.reduce(
    (sum, event) => sum + parseAmount(event.data?.amount ?? event.amount),
    0
  );
  const refunded = paymentRefunded || eventRefunded;

  if (!refunded) return null;

  const total = getPaidCryptoTotal(charge);
  const currency = hasPayment(charge)
    ? currencyDisplaySymbol(getPaymentCurrency(charge))
    : '';

  return {
    refunded,
    total,
    currency,
    isPartial: total > 0 && refunded < total
  };
};

export const generateKey = prefix => {
  const key = btoa(
    Array.from(new Array(20))
      .map(() => Math.floor(Math.random() * (999 - 0) + 0).toString(16))
      .join('')
  );

  return prefix ? `${prefix}_${key}` : key;
};

export const currencyFromProtocol = protocol => {
  const matches = config.currencies.filter(c => c.protocol === protocol);
  return matches.length > 0 ? matches[0] : null;
};

const protocolFromCharge = (charge, currencyFrom) => {
  let coin;
  Object.keys(charge.quotes).forEach(protocol => {
    const quote = charge.quotes[protocol];

    if (quote.currency === currencyFrom) {
      coin = protocol;
    }
  });
  return coin;
};

export const currencyForTimeline = (event, charge) => {
  const { exchanges, payments } = charge;
  let coin = event.type.split('_')[0].toLowerCase();

  if (coin === 'changelly' && exchanges && exchanges.changelly) {
    const currencyFrom = exchanges.changelly.currencyFrom.toUpperCase();
    if (payments && Object.keys(payments).length) {
      // There are payments
      if (Object.keys(payments)[0] !== coin) {
        coin = protocolFromCharge(charge, currencyFrom);
      } else {
        coin = Object.keys(payments)[0];
      }
    } else {
      // No payments!
      coin = protocolFromCharge(charge, currencyFrom);
    }
  } else if (payments && Object.keys(payments).length) {
    coin = Object.keys(payments)[0];
  }
  return coin;
};

export const isChangelly = (charge) => {
  return !!(charge.exchanges && charge.exchanges.changelly);
};

export const changellyUrl = (charge) => {
  return isChangelly(charge) ? `https://changelly.com/transaction/${charge.exchanges.changelly.id}` : null;
};

export const changellyQuote = (charge) => {
  const { exchanges, quotes } = charge;
  if (isChangelly(charge)) {
    const currencyFrom = exchanges.changelly.currencyFrom.toUpperCase();
    const protocol = protocolFromCharge(charge, currencyFrom);
    const quote = quotes[protocol];
    return quote;
  }
  return null;
};

export const downloadReport = async (data, fileName, client) => {
  const { accessToken } = await client.get('authentication');
  const url = `${config.apiUrl}/reports`;
  const options = {
    method: 'POST',
    body: JSON.stringify(data),
    headers: new window.Headers({
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      Accept: 'text/csv'
    })
  };

  return downloadFile(url, options, fileName);
};

export const downloadFile = async (url, options, fileName) => {
  const response = await window.fetch(url, options);
  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.target = '_blank';
  link.download = fileName;
  document.body.appendChild(link);

  link.click();
  link.remove();
};

export function hasLoader (loaderName = '') {
  return function decorator (target, name, descriptor) {
    const method = descriptor.value;
    const statusKey = `${loaderName}Status`;
    const statuses = new WeakMap();

    const getStatus = instance => {
      if (!statuses.has(instance)) {
        statuses.set(instance, observable({
          isLoading: false,
          error: null
        }));
      }

      return statuses.get(instance);
    };

    if (typeof method === 'function') {
      Object.defineProperty(target, statusKey, {
        configurable: true,
        get () {
          return getStatus(this);
        }
      });

      descriptor.value = async function (...args) {
        const status = getStatus(this);

        try {
          runInAction(() => {
            status.isLoading = true;
            status.error = null;
          });
          const result = await method.apply(this, args);
          runInAction(() => {
            status.isLoading = false;
          });
          return result;
        } catch (e) {
          runInAction(() => {
            status.isLoading = false;
            status.error = e;
          });
          console.log(`Error: ${e}`);
          throw e;
        }
      };
    }

    return descriptor;
  };
}

export const copyToClipboard = value => {
  const textArea = document.createElement('textarea');

  // textArea.value = value;
  textArea.innerHTML = value;
  textArea.style.position = 'absolute';
  textArea.style.zIndex = -1;
  textArea.setAttribute('readonly', '');
  document.body.appendChild(textArea);
  textArea.select();
  navigator.clipboard.writeText(textArea.value);
  document.body.removeChild(textArea);
};

export const media = window && window.matchMedia
  ? {
      mobile: window.matchMedia('(max-width: 480px)').matches,
      tablet: window.matchMedia('(min-width: 481px) and (max-width: 800)').matches,
      desktop: window.matchMedia('(min-width: 801px)').matches
    }
  : {};

export const retryLoadingChunk = (fn, retriesLeft = 5, interval = 1000) => {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            if (window.location.hash !== '#retry') {
              // the chunk might no longer be available due to a recent redeployment of the page
              // mark the page to don't trigger reload infinitely
              window.location.hash = '#retry';
              window.location.reload(true);
            } else {
              reject(error);
            }
            return;
          }

          // Passing on "reject" is the important part
          retryLoadingChunk(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
};

export const getPercentageChange = (a, b) => parseFloat(
  ((((a - b) / a) * 100) * -1).toFixed(2)
);
